<template>
  <div :class="rootClasses">
    <section
      v-if="!embedded"
      class="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-500 to-purple-500"
    >
      <div class="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.2),_transparent_50%)]"></div>
      <div class="relative mx-auto max-w-6xl px-6 py-12 text-white">
        <div class="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div class="max-w-2xl">
            <div class="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1 text-xs font-semibold uppercase tracking-wide">
              <vue-feather type="zap" class="h-4 w-4" />
              Jack Access Control Center
            </div>
            <h1 class="mt-4 text-3xl font-bold sm:text-4xl">Fast-track store support without compromising safety</h1>
            <p class="mt-3 text-sm text-white/90 sm:text-base">
              Issue time-boxed access tokens for trusted teammates, monitor their usage live and revoke instantly when the mission is complete.
            </p>
          </div>
          <div class="grid w-full gap-4 rounded-2xl bg-white/15 p-5 text-sm backdrop-blur lg:w-80">
            <div class="flex items-center justify-between text-white/90">
              <span>Active grants</span>
              <span class="text-2xl font-semibold">{{ activeCount }}</span>
            </div>
            <div class="flex items-center justify-between text-white/90">
              <span>Expiring soon</span>
              <span class="text-2xl font-semibold">{{ expiringSoonCount }}</span>
            </div>
            <div class="flex items-center justify-between text-white/90">
              <span>Last sync</span>
              <span class="text-right text-xs text-white/80">
                <span v-if="lastActiveSyncLabel">{{ lastActiveSyncLabel }}</span>
                <span v-else>Awaiting data…</span>
              </span>
            </div>
          </div>
        </div>

        <div class="mt-10 flex flex-wrap items-center gap-3 text-sm">
          <div class="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2">
            <vue-feather type="shield" class="h-4 w-4" />
            {{ isSuperAdmin ? 'Super admin' : 'Scoped admin' }}
          </div>
          <div class="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2">
            <vue-feather type="activity" class="h-4 w-4" />
            Logs auto-refresh on every action
          </div>
          <hs-button variant="secondary" icon="plus" @click="handleOpenCreate">
            New Jack access
          </hs-button>
        </div>
      </div>
    </section>

    <main :class="mainContainerClasses">
      <div v-if="embedded" class="space-y-6">
        <div class="rounded-xl bg-white p-6 shadow-sm dark:bg-slate-800">
          <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 class="text-lg font-semibold text-slate-800 dark:text-white">Jack Access</h2>
              <p class="text-sm text-slate-500 dark:text-slate-300">
                Manage on-demand store access with live auditing.
              </p>
            </div>
            <hs-button icon="plus" @click="handleOpenCreate">
              New Jack access
            </hs-button>
          </div>
        </div>

        <div class="grid gap-4 rounded-xl bg-white p-6 shadow-sm dark:bg-slate-800 md:grid-cols-3">
          <div class="flex flex-col gap-1">
            <span class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Active grants</span>
            <span class="text-2xl font-semibold text-slate-800 dark:text-white">{{ activeCount }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Expiring soon</span>
            <span class="text-2xl font-semibold text-amber-500 dark:text-amber-300">{{ expiringSoonCount }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">Last sync</span>
            <span class="text-sm text-slate-600 dark:text-slate-200">
              <span v-if="lastActiveSyncLabel">{{ lastActiveSyncLabel }}</span>
              <span v-else>Awaiting data…</span>
            </span>
          </div>
        </div>
      </div>
      <div class="grid gap-6 lg:grid-cols-[1.6fr_1fr] pt-6">
        <section class="flex flex-col gap-6">
          <div class="relative rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-slate-800">
            <div class="flex flex-wrap items-end gap-4">
              <div class="flex min-w-[260px] flex-col">
                <async-store-select
                  :model-value="selectedStoreSummary"
                  :include-all-option="true"
                  placeholder="Search stores"
                  @update:modelValue="handleStoreSelection"
                />
              </div>
              <div class="flex-1 min-w-[220px]">
                <hs-input
                  v-model="searchQuery"
                  label="Search"
                  type="search"
                  icon="search"
                  placeholder="Filter by name, phone or reason"
                />
              </div>
              <div class="flex items-end gap-3">
                <hs-button variant="secondary" icon="refresh-cw" @click="handleRefresh">
                  Refresh
                </hs-button>
                 
              </div>
            </div>
          </div>

          <div class="space-y-4">
            <div v-if="loadingActive" class="space-y-3">
              <div v-for="index in 3" :key="`skeleton-${index}`" class="h-32 animate-pulse rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-slate-800"></div>
            </div>

            <hs-empty-state
              v-else-if="!filteredActiveAccesses.length"
              icon="key"
              title="No live Jack grants"
              description="Create a grant to issue temporary support access for this store."
            >
              <template #action>
                <hs-button icon="plus" @click="handleOpenCreate">New Jack access</hs-button>
              </template>
            </hs-empty-state>

            <article
              v-for="access in filteredActiveAccesses"
              :key="access.id"
              class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-blue-200 hover:shadow-md dark:border-gray-700 dark:bg-slate-800"
            >
              <div class="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                    <vue-feather type="user" class="h-5 w-5 text-blue-500" />
                    <span>
                      {{ access.user?.names || `User #${access.target_user_id}` }}
                      <span v-if="access.user?.phone_number" class="text-sm font-normal text-gray-500 dark:text-gray-400">
                        ({{ access.user.phone_number }})
                      </span>
                    </span>
                  </div>
                  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Reason: {{ access.reason }}</p> 
                </div>

                <div class="text-right text-sm text-gray-500 dark:text-gray-400">
                  <hs-badge :tone="expiryTone(access)">{{ expiryLabel(access) }}</hs-badge>
                  <div class="text-xs">Created {{ formatRelative(access.created_at) }}</div>
                </div>
              </div>

              <div class="mt-4 flex flex-wrap gap-2">
                <hs-badge
                  v-for="permission in access.granted_permissions"
                  :key="permission"
                  :tone="permissionBadgeTone(permission)"
                  :icon="isElevated(permission) ? 'unlock' : 'eye'"
                  size="sm"
                >
                  {{ permissionLabel(permission) }}
                </hs-badge>
              </div>

              <div class="mt-6 flex flex-wrap items-center justify-between gap-3">
                <div class="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                  <span>Access ID: {{ access.id }}</span>
                  <span>Recorder: {{ access.recorded_by_id }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <hs-button variant="ghost" size="sm" icon="activity" @click="handleViewLogs(access)">
                    View activity
                  </hs-button>
                  <hs-button
                    v-if="isSuperAdmin"
                    variant="ghost"
                    size="sm"
                    icon="shield-off"
                    @click="handleRequestRevoke(access)"
                  >
                    Revoke
                  </hs-button>
                </div>
              </div>
            </article>
          </div>
        </section>

        <jack-log-timeline
          :logs="jackLogs"
          :loading="loadingLogs"
          :highlight-access-id="selectedAccessIdForLogs"
        >
          <template #actions>
            <div class="flex items-center gap-2">
              <hs-button
                v-if="selectedAccessIdForLogs"
                variant="ghost"
                size="sm"
                @click="handleShowAllLogs"
              >
                View store-wide logs
              </hs-button>
              <div class="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-slate-900 dark:text-gray-300">
                {{ lastLogsSyncLabel }}
              </div>
            </div>
          </template>
        </jack-log-timeline>
      </div>
    </main>

    <jack-access-modal
      v-if="showCreateModal"
      :selected-store-id="selectedStoreId"
      :selected-store="selectedStoreSummary"
      :store-members="modalStoreMembers"
      :store-members-loading="storeMembersLoading"
      :permission-groups="permissionGroups"
      :permission-presets="permissionPresets"
      :creating="creatingAccess"
      :is-super-admin="isSuperAdmin"
      @close="showCreateModal = false"
      @store-change="handleModalStoreChange"
      @submit="handleCreateAccess"
    />

    <confirmation-modal
      :is-open="showRevokeModal"
      title="Revoke Jack access"
      :message="revokeMessage"
      confirm-text="Revoke"
      cancel-text="Cancel"
      type="danger"
      :loading="revokingAccess"
      @close="handleCloseRevoke"
      @confirm="handleConfirmRevoke"
    />
  </div>
</template>

<script>
import { addMinutes, formatDistanceToNowStrict, isBefore } from "date-fns";
import { toast } from "vue3-toastify";
import JackAccessModal from "@/components/jack/JackAccessModal.vue";
import JackLogTimeline from "@/components/jack/JackLogTimeline.vue";
import AsyncStoreSelect from "@/components/jack/AsyncStoreSelect.vue";
import { HsBadge, HsButton, HsEmptyState, HsInput } from "@/components/ui";

const createAllStoresOption = () => ({ id: null, name: "All stores", isAll: true });

const ELEVATED_PATTERN = /(WRITE|MANAGE|APPROVE|PROCESS|CHANGE)/i;

export default {
  name: "AdminJackView",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    JackAccessModal,
    JackLogTimeline,
    AsyncStoreSelect,
    HsBadge,
    HsButton,
    HsEmptyState,
    HsInput,
  },
  data() {
    return {
      selectedStoreId: null,
      selectedStoreSummary: createAllStoresOption(),
      searchQuery: "",
      showCreateModal: false,
      selectedAccessIdForLogs: null,
      showRevokeModal: false,
      pendingRevokeAccess: null,
      isSuperAdmin: false,
    };
  },
  computed: {
    rootClasses() {
      if (this.embedded) {
        return "bg-transparent pb-6 dark:bg-transparent";
      }
      return "min-h-screen bg-gray-50 pb-12 dark:bg-slate-900";
    },
    mainContainerClasses() {
      const base = ["mx-auto", "max-w-6xl"];
      if (this.embedded) {
        return [...base, "py-6"].join(" ");
      }
      return [...base, "mt-[-30px]"].join(" ");
    },
    activeAccesses() {
      return this.$store.getters.jackActiveAccesses || [];
    },
    loadingActive() {
      return this.$store.getters.isLoadingJackActiveAccesses;
    },
    creatingAccess() {
      return this.$store.getters.isCreatingJackAccess;
    },
    revokingAccess() {
      return this.$store.getters.isRevokingJackAccess;
    },
    permissionGroups() {
      return this.$store.getters.jackPermissionGroups || [];
    },
    permissionPresets() {
      return this.$store.getters.jackPermissionPresets || [];
    },
    permissionLabelMap() {
      const map = new Map();
      (this.$store.getters.jackPermissionOptions || []).forEach((option) => {
        map.set(option.value, option);
      });
      return map;
    },
    jackLogs() {
      return this.$store.getters.jackLogs || [];
    },
    loadingLogs() {
      return this.$store.getters.isLoadingJackLogs;
    },
    modalStoreMembers() {
      if (this.selectedStoreId == null) {
        return [];
      }
      const getter = this.$store.getters.jackStoreMembers;
      return getter ? getter(this.selectedStoreId) : [];
    },
    storeMembersLoading() {
      return this.$store.getters.isLoadingJackStoreMembers;
    },
    filteredActiveAccesses() {
      const query = (this.searchQuery || "").trim().toLowerCase();
      const items = [...this.activeAccesses];
      const sorted = items.sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at));
      if (!query) {
        return sorted;
      }
      return sorted.filter((access) => {
        const haystack = [
          access.user?.names,
          access.user?.phone_number,
          access.reason,
          access.granted_permissions?.join(" "),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      });
    },
    activeCount() {
      return this.activeAccesses.length;
    },
    expiringSoonCount() {
      const cutoff = addMinutes(new Date(), 60);
      return this.activeAccesses.filter((access) => isBefore(new Date(access.expires_at), cutoff)).length;
    },
    lastActiveSyncLabel() {
      const timestamp = this.$store.getters.jackLastActiveSyncAt;
      if (!timestamp) {
        return null;
      }
      return `${formatDistanceToNowStrict(new Date(timestamp))} ago`;
    },
    lastLogsSyncLabel() {
      const timestamp = this.$store.getters.jackLastLogsSyncAt;
      if (!timestamp) {
        return "Logs idle";
      }
      return `Logs updated ${formatDistanceToNowStrict(new Date(timestamp))} ago`;
    },
    revokeMessage() {
      if (!this.pendingRevokeAccess) {
        return "";
      }
      const userLabel = this.pendingRevokeAccess.user?.names || this.pendingRevokeAccess.user?.phone_number || `User #${this.pendingRevokeAccess.target_user_id}`;
      return `Revoke Jack access for ${userLabel}? Their session will terminate immediately.`;
    },
  },
  watch: {
    selectedStoreId: {
      immediate: true,
      handler(storeId) {
        if (storeId == null) {
          this.refreshActiveAccesses();
          this.refreshLogs();
          return;
        }
        this.refreshActiveAccesses();
        this.refreshLogs();
      },
    },
  },
  async mounted() {
    await this.bootstrap();
  },
  methods: {
    async bootstrap() {
      try {
        await this.$store.dispatch("fetchJackPermissionOptions");
      } catch (error) {
        toast.error(error?.response?.data?.error?.message || "Failed to load permissions metadata");
      }

      try {
        const permissions = await this.$store.dispatch("checkUserPermissions");
        this.isSuperAdmin = Array.isArray(permissions) && permissions.includes("SUPER_ADMIN");
      } catch (error) {
        this.isSuperAdmin = false;
      }
    },
    handleStoreSelection(store) {
      if (store && typeof store === "object" && store.id != null) {
        this.selectedStoreSummary = { ...store };
        this.selectedStoreId = Number(store.id);
      } else {
        this.selectedStoreSummary = createAllStoresOption();
        this.selectedStoreId = null;
      }
      this.selectedAccessIdForLogs = null;
    },
    permissionLabel(permission) {
      return this.permissionLabelMap.get(permission)?.label || permission;
    },
    isElevated(permission) {
      return ELEVATED_PATTERN.test(permission || "");
    },
    permissionBadgeTone(permission) {
      return this.isElevated(permission) ? "warning" : "success";
    },
    expiryLabel(access) {
      return `Expires ${this.formatRelative(access.expires_at)}`;
    },
    expiryTone(access) {
      return this.isExpiringSoon(access) ? "warning" : "success";
    },
    expiryBadgeClass(access) {
      const soon = this.isExpiringSoon(access);
      return [
        "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide",
        soon
          ? "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-200"
          : "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-200",
      ];
    },
    isExpiringSoon(access) {
      const expiresAt = new Date(access.expires_at);
      const threshold = addMinutes(new Date(), 90);
      return isBefore(expiresAt, threshold);
    },
    formatRelative(value) {
      try {
        return formatDistanceToNowStrict(new Date(value || Date.now()), { addSuffix: true });
      } catch (error) {
        return "soon";
      }
    },
    async refreshActiveAccesses() {
      try {
        const storeId = this.selectedStoreId == null ? null : this.selectedStoreId;
        await this.$store.dispatch("fetchJackActiveAccesses", {
          storeId,
        });
      } catch (error) {
        toast.error(error?.response?.data?.error?.message || "Could not refresh Jack accesses");
      }
    },
    async refreshLogs(params = {}) {
      try {
        const base = this.selectedStoreId == null ? {} : { storeId: this.selectedStoreId };
        await this.$store.dispatch("fetchJackLogs", {
          ...base,
          ...params,
        });
      } catch (error) {
        toast.error(error?.response?.data?.error?.message || "Failed to load Jack logs");
      }
    },
    async fetchStoreMembers(storeId) {
      if (storeId == null) {
        return;
      }
      try {
        await this.$store.dispatch("fetchJackStoreMembers", { storeId });
      } catch (error) {
        toast.error(error?.response?.data?.error?.message || "Unable to load store teammates");
      }
    },
    handleOpenCreate() {
      if (this.selectedStoreId != null && !this.modalStoreMembers.length) {
        this.fetchStoreMembers(this.selectedStoreId);
      }
      this.showCreateModal = true;
    },
    handleModalStoreChange(payload) {
      let storeId = null;
      let storeSummary = null;

      if (payload && typeof payload === "object") {
        storeId =
          payload.storeId ??
          (payload.store && payload.store.id != null ? payload.store.id : null);
        if (payload.store && payload.store.id != null) {
          storeSummary = { ...payload.store };
        }
      } else if (payload != null && payload !== "") {
        storeId = payload;
      }

      if (storeId != null && storeId !== "") {
        this.selectedStoreId = Number(storeId);
        if (storeSummary) {
          this.selectedStoreSummary = { ...storeSummary };
        }
      } else {
        this.selectedStoreId = null;
        this.selectedStoreSummary = createAllStoresOption();
      }
      this.selectedAccessIdForLogs = null;
    },
    async handleCreateAccess(payload) {
      try {
        const result = await this.$store.dispatch("createJackStoreAccess", payload);
        toast.success("Jack access granted");
        this.showCreateModal = false;
        this.selectedStoreId = payload.store_id;
        this.selectedAccessIdForLogs = result?.id || null;
        await this.refreshLogs({ jackAccessId: result?.id });
      } catch (error) {
        toast.error(error?.response?.data?.error?.message || "Could not create Jack access");
      }
    },
    handleRefresh() {
      this.refreshActiveAccesses();
      this.refreshLogs(
        this.selectedAccessIdForLogs
          ? { jackAccessId: this.selectedAccessIdForLogs }
          : {}
      );
    },
    handleViewLogs(access) {
      this.selectedAccessIdForLogs = access.id;
      this.refreshLogs({ jackAccessId: access.id });
    },
    handleShowAllLogs() {
      this.selectedAccessIdForLogs = null;
      this.refreshLogs();
    },
    handleRequestRevoke(access) {
      this.pendingRevokeAccess = access;
      this.showRevokeModal = true;
    },
    handleCloseRevoke() {
      this.pendingRevokeAccess = null;
      this.showRevokeModal = false;
    },
    async handleConfirmRevoke() {
      if (!this.pendingRevokeAccess) {
        return;
      }
      try {
        await this.$store.dispatch("revokeJackStoreAccess", {
          jackAccessId: this.pendingRevokeAccess.id,
        });
        toast.success("Jack access revoked");
        if (this.selectedAccessIdForLogs === this.pendingRevokeAccess.id) {
          this.selectedAccessIdForLogs = null;
          this.refreshLogs();
        }
      } catch (error) {
        toast.error(error?.response?.data?.error?.message || "Failed to revoke access");
      } finally {
        this.handleCloseRevoke();
      }
    },
  },
};
</script>

<style scoped>
:deep(.rounded-xl),
:deep(.rounded-2xl),
:deep(.rounded-lg) {
  border-radius: 14px;
  box-shadow: none;
}

:deep(.bg-white),
:deep(.dark\:bg-slate-800) {
  background: color-mix(in srgb, var(--hs-surface, #fff) 94%, transparent);
}

:deep(input) {
  border-radius: 15px;
}
</style>
